import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ROUTES, INSIGHTS_SECTIONS, insightsSectionUrl } from '@/constants';
import { CTA_KEYS } from '@/constants/cta';
import { useToast } from '@/contexts/ToastContext';
import { useAuthModal } from '@/contexts/AuthModalContext';
import { analytics } from '@/utils/analytics';
import { isAuthenticated, isAdmin as checkIsAdmin, getUserEmail, clearSession } from '@/utils/sessionStorage';
import '@/styles/features/home/Header.css';

const NAV_LINKS: { label: string; path: string }[] = [
  { label: 'Offerings', path: ROUTES.OFFERINGS },
  { label: 'Success Stories', path: ROUTES.SUCCESS_STORIES },
  { label: 'Accelerators', path: ROUTES.ACCELERATORS },
  { label: 'Migration Estimator', path: ROUTES.MIGRATION_ESTIMATOR },
  // Why Winter tab disabled — keep for later re-enable.
  // { label: 'Why Winter', path: ROUTES.WHY_WINTER },
  { label: 'Events', path: ROUTES.EVENTS },
  { label: 'About', path: ROUTES.ABOUT },
];

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const { openLoginModal } = useAuthModal();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [insightsMenuOpen, setInsightsMenuOpen] = useState(false);

  const isCalendarDownloadPage = /^\/events\/[^/]+\/calendar$/.test(location.pathname);

  useEffect(() => {
    const checkAuthStatus = () => {
      setIsLoggedIn(isAuthenticated());
      setUserEmail(getUserEmail() || '');
      setIsAdmin(checkIsAdmin());
    };
    checkAuthStatus();
    const handleTokenExpired = () => checkAuthStatus();
    globalThis.addEventListener('token-expired', handleTokenExpired);
    return () => {
      globalThis.removeEventListener('token-expired', handleTokenExpired);
    };
  }, []);

  // Close menus on route or hash change.
  useEffect(() => {
    setShowMobileNav(false);
    setShowUserMenu(false);
    setInsightsMenuOpen(false);
  }, [location.pathname, location.hash]);

  const handleLoginClick = (e: React.MouseEvent) => {
    e.preventDefault();
    openLoginModal();
  };

  const handleLogout = () => {
    clearSession();
    setIsLoggedIn(false);
    setUserEmail('');
    setIsAdmin(false);
    setShowUserMenu(false);
    showToast('Logged out successfully', 'success');
    setTimeout(() => {
      globalThis.location.reload();
    }, 500);
  };

  const getInitials = (email: string) => (email ? email.charAt(0).toUpperCase() : 'U');

  const isActivePath = (path: string) => {
    if (path === ROUTES.OFFERINGS) {
      return location.pathname === ROUTES.HOME || location.pathname === '/offerings';
    }
    if (path === ROUTES.EVENTS) {
      return location.pathname === ROUTES.EVENTS || location.pathname === ROUTES.EVENTS_ON_DEMAND;
    }
    return location.pathname === path;
  };

  const isInsightsActive = location.pathname === ROUTES.INSIGHTS;

  if (isCalendarDownloadPage) return null;

  return (
    <nav className="nav">
      <div className="nav-inner">
        <Link to={ROUTES.HOME} className="brand" aria-label="Winter × MongoDB home">
          <img className="brand-mark" src="/assets/winter/winterMark.png" alt="winter" />
          <span className="brand-divider" />
          <img className="brand-mdb" src="/assets/winter/mdbLogo-new.svg" alt="MongoDB" />
        </Link>

        {/* Desktop / tablet nav */}
        <div className="nav-links">
          {NAV_LINKS.slice(0, 4).map((l) => (
            <Link
              key={l.path}
              to={l.path}
              className={'nav-link' + (isActivePath(l.path) ? ' active' : '')}
            >
              {l.label}
            </Link>
          ))}

          <div
            className={
              'nav-dropdown' +
              (isInsightsActive ? ' active' : '') +
              (insightsMenuOpen ? ' is-open' : '')
            }
            onMouseEnter={() => setInsightsMenuOpen(true)}
            onMouseLeave={() => setInsightsMenuOpen(false)}
          >
            <Link
              to={ROUTES.INSIGHTS}
              className={'nav-link nav-dropdown-trigger' + (isInsightsActive ? ' active' : '')}
              aria-haspopup="true"
              aria-expanded={insightsMenuOpen}
              onFocus={() => setInsightsMenuOpen(true)}
            >
              Insights
              <svg className="nav-dropdown-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </Link>
            <div className="nav-dropdown-menu" role="menu">
              <div className="nav-dropdown-panel">
                {INSIGHTS_SECTIONS.map((section) => (
                  <Link
                    key={section.hash}
                    to={insightsSectionUrl(section.hash)}
                    className="nav-dropdown-item"
                    role="menuitem"
                    onClick={() => setInsightsMenuOpen(false)}
                  >
                    {section.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {NAV_LINKS.slice(4).map((l) => (
            <Link
              key={l.path}
              to={l.path}
              className={'nav-link' + (isActivePath(l.path) ? ' active' : '')}
            >
              {l.label}
            </Link>
          ))}

          {isLoggedIn ? (
            <div className="user-menu-container">
              <button
                type="button"
                className="user-avatar-button"
                onClick={() => setShowUserMenu((v) => !v)}
                aria-label="User menu"
              >
                <span className="user-avatar">{getInitials(userEmail)}</span>
              </button>
              {showUserMenu && (
                <>
                  <button
                    type="button"
                    className="user-menu-overlay"
                    onClick={() => setShowUserMenu(false)}
                    aria-label="Close menu"
                  />
                  <div className="user-menu-dropdown" role="menu">
                    <div className="user-menu-header">
                      <div className="user-menu-email">{userEmail}</div>
                    </div>
                    <div className="user-menu-divider" />
                    <button
                      type="button"
                      className="user-menu-item"
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/profile');
                      }}
                    >
                      Profile
                    </button>
                    {isAdmin && (
                      <>
                        <div className="user-menu-divider" />
                        <Link
                          to={ROUTES.ADMIN}
                          className="user-menu-item"
                          onClick={() => setShowUserMenu(false)}
                        >
                          Admin Dashboard
                        </Link>
                      </>
                    )}
                    <div className="user-menu-divider" />
                    <button
                      type="button"
                      className="user-menu-item user-menu-logout"
                      onClick={handleLogout}
                    >
                      Logout
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={handleLoginClick}
              className="nav-link nav-login"
              style={{ marginLeft: 8 }}
            >
              Login
            </button>
          )}
          <button
            type="button"
            className="nav-cta"
            onClick={() => {
              analytics.trackCTAClick(CTA_KEYS.talkToArchitect, 'Header');
              navigate('/contact');
            }}
          >
            Talk to an Architect
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Mobile burger */}
        <button
          type="button"
          className={'nav-burger' + (showMobileNav ? ' open' : '')}
          aria-label="Toggle navigation"
          aria-expanded={showMobileNav}
          onClick={() => setShowMobileNav((v) => !v)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      {/* Mobile sheet */}
      {showMobileNav && (
        <div className="nav-mobile-sheet" role="menu">
          {NAV_LINKS.slice(0, 4).map((l) => (
            <Link
              key={l.path}
              to={l.path}
              className={'nav-mobile-link' + (isActivePath(l.path) ? ' active' : '')}
            >
              {l.label}
            </Link>
          ))}

          <Link
            to={ROUTES.INSIGHTS}
            className={'nav-mobile-link' + (isInsightsActive ? ' active' : '')}
          >
            Insights
          </Link>
          <div className="nav-mobile-sub">
            {INSIGHTS_SECTIONS.map((section) => (
              <Link
                key={section.hash}
                to={insightsSectionUrl(section.hash)}
                className="nav-mobile-sublink"
                onClick={() => setShowMobileNav(false)}
              >
                {section.label}
              </Link>
            ))}
          </div>

          {NAV_LINKS.slice(4).map((l) => (
            <Link
              key={l.path}
              to={l.path}
              className={'nav-mobile-link' + (isActivePath(l.path) ? ' active' : '')}
            >
              {l.label}
            </Link>
          ))}

          {isAdmin && (
            <Link to={ROUTES.ADMIN} className="nav-mobile-link">
              Admin
            </Link>
          )}
          <div className="nav-mobile-actions">
            {isLoggedIn ? (
              <>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => navigate('/profile')}
                >
                  {userEmail || 'Profile'}
                </button>
                <button type="button" className="btn btn-ghost" onClick={handleLogout}>
                  Logout
                </button>
              </>
            ) : (
              <button type="button" className="btn btn-ghost" onClick={handleLoginClick}>
                Login
              </button>
            )}
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                analytics.trackCTAClick(CTA_KEYS.talkToArchitect, 'Header (mobile)');
                navigate('/contact');
              }}
            >
              Talk to an Architect
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Header;
